import React, { useEffect, useState, useRef } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

import { addNotificationAction } from '../../action/notifications/notifications';
import { loadOrg } from '../../organization/actions/load-org';
import { orgSelector } from '../../reducers/org-selectors';
import { updateEmployeeDirectoryImport } from '../actions/update-employee-directory-import';

import { uploadFileViaEmployer } from '../../api/file-upload-api-client';
import {
  getFileKey,
  isSupportedFileExtension,
  PERMITTED_FILE_TYPES,
} from '../../employees/employee-import-utils';

import { FILES_BUCKET } from '../../config/aws-config';
import { combineWords } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  org: orgSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, employeeDirectoryImportId }: InputProps,
) => ({
  actions: {
    loadOrg: () => dispatch(loadOrg(orgId)),
    updateEmployeeDirectoryImport: (update: EmployeeDirectoryImportUpdateRequest) =>
      dispatch(updateEmployeeDirectoryImport(employeeDirectoryImportId, update)),
    addNotification: (settings: { key: string; message: string }) =>
      dispatch(addNotificationAction(settings)),
  },
});

type InputProps = {
  orgId: number;
  employeeDirectoryImportId: number;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeDirectoryImportUpdateButton = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { actions, org, employeeDirectoryImportId } = props;
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const inputRef = useRef<any>();

  useEffect(() => {
    actions.loadOrg();
  });

  if (!org) {
    return <LoadingMessage />;
  }
  const handleUploadClick = async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
    if (!files || files.length === 0) {
      return;
    }
    const file = files[0];
    setIsUploadingFile(true);
    if (!isSupportedFileExtension(file)) {
      actions.addNotification({
        key: `update-employee-list-error-${employeeDirectoryImportId}`,
        message: `Invalid file. Please upload a file ending in ${combineWords(
          PERMITTED_FILE_TYPES,
          'or',
        )}`,
      });
      return;
    }

    const fileKey = getFileKey(file, org);

    try {
      await uploadFileViaEmployer(fileKey, file);
      actions.updateEmployeeDirectoryImport({
        employeeListPath: `${FILES_BUCKET}/${fileKey}`,
      });
      actions.addNotification({
        key: `update-employee-list-${employeeDirectoryImportId}`,
        message: 'Successfully updated employee import list file',
      });
    } catch (e) {
      setIsUploadingFile(false);
      return;
    }

    setIsUploadingFile(false);
  };

  return (
    <Tooltip title="Update File">
      <label htmlFor={`contained-button-file-${employeeDirectoryImportId}`}>
        <input
          className="u-display-none"
          ref={inputRef}
          hidden
          id={`contained-button-file-${employeeDirectoryImportId}`}
          type="file"
          onChange={handleUploadClick}
        />
        {/* Adding the span component makes the entire <IconButton> be "clickable", which
          will trigger the input, since the component is within the label tag. */}
        <IconButton component="span" disabled={isUploadingFile}>
          <MaterialDesignIcon iconName={ICON_NAMES.EDIT} />
        </IconButton>
      </label>
    </Tooltip>
  );
});
